import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";

import { errorMessage } from "../lib/api";
import { hasPlace, placeLine } from "../lib/format";
import { qk, useDeleteEntry, usePatchEntry, useRetryEntry } from "../lib/queries";
import type { Entry } from "../lib/types";
import { DateStamp } from "./DateStamp";
import { PhotoMount } from "./PhotoMount";
import { RarityBadge } from "./RarityBadge";
import { useToasts } from "./Toasts";

export type EntryCardProps = {
  entry: Entry;
  /** Play the one-time reveal, straight after classification lands. */
  revealing?: boolean;
  eager?: boolean;
  className?: string;
};

/**
 * One find, as it appears in the scrapbook feed: the print mounted on card, the
 * name in Fraunces, the binomial beneath it, a stamped date, and the opening of
 * the field note.
 */
export function EntryCard({ entry, revealing = false, eager = false, className }: EntryCardProps) {
  const client = useQueryClient();
  const { push } = useToasts();
  const patch = usePatchEntry(entry.id);
  const remove = useDeleteEntry();
  const retry = useRetryEntry();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const where = placeLine(entry.place);
  const isRejected = entry.status === "rejected";
  const isFailed = entry.status === "failed";
  const href = `/entry/${entry.id}`;
  // No place means no collection. Say so on the card, not just on the page.
  const unfiled = !hasPlace(entry) && entry.status === "complete";

  const refreshImage = () => {
    void client.invalidateQueries({ queryKey: qk.entry(entry.id) });
    void client.invalidateQueries({ queryKey: ["entries"] });
  };

  const onDelete = () => {
    remove.mutate(entry.id, {
      onSuccess: () => push({ title: "That page has been taken out of the book." }),
      onError: (error) =>
        push({ kind: "problem", title: "The page wouldn't come out.", body: errorMessage(error) }),
    });
  };

  return (
    <article
      className={clsx(
        "sheet grain relative overflow-hidden p-3 pb-4 transition-shadow duration-300 hover:shadow-lifted",
        className,
      )}
    >
      {/* Mouse convenience only: the heading link below is the keyboard route. */}
      <Link to={href} tabIndex={-1} className="block">
        <PhotoMount
          seedId={entry.id}
          src={entry.thumbUrl ?? entry.imageUrl}
          alt={entry.displayTitle}
          revealing={revealing}
          onImageError={refreshImage}
          eager={eager}
        >
          {entry.rarity !== null && entry.rarity >= 4 ? (
            <span className="absolute top-2 left-2 bg-[color-mix(in_oklab,var(--color-card)_88%,transparent)] px-1.5 py-1">
              <RarityBadge rarity={entry.rarity} pipsOnly />
            </span>
          ) : null}
        </PhotoMount>
      </Link>

      <div className={clsx("mt-5 px-1", revealing && "animate-settle")}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="display-5 text-[1.375rem] leading-tight text-ink">
              <Link to={href} className="hover:text-ochre">
                {entry.displayTitle}
              </Link>
            </h3>
            {entry.scientificName ? (
              <p className="mt-0.5 font-prose text-[0.9375rem] italic text-ink-soft">
                {entry.scientificName}
              </p>
            ) : null}
          </div>

          <FavouriteButton
            favourite={entry.favourite}
            title={entry.displayTitle}
            onToggle={() => patch.mutate({ favourite: !entry.favourite })}
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <DateStamp date={entry.foundAt} seedId={entry.id} size="sm" />
          {where ? (
            <span className="field-label-tight text-ink-soft">{where}</span>
          ) : unfiled ? (
            <Link to={href} className="field-label-tight text-ochre underline decoration-rule underline-offset-4">
              Unfiled — add a place
            </Link>
          ) : null}
        </div>

        {entry.notes ? (
          <>
            <div className="hairline my-3.5" />
            <p className="line-clamp-2 font-prose text-[1.0625rem] leading-relaxed text-ink">
              {entry.notes}
            </p>
          </>
        ) : null}

        {entry.safetyNote ? (
          <p className="mt-3 flex items-start gap-2 font-prose text-sm leading-snug text-rust">
            <span aria-hidden="true" className="mt-[0.15rem] block h-3 w-1 shrink-0 bg-rust" />
            <span>
              <span className="sr-only">Safety note: </span>
              {entry.safetyNote}
            </span>
          </p>
        ) : null}

        {isRejected ? (
          <RejectedNotice
            reason={entry.rejectionReason}
            confirming={confirmingDelete}
            deleting={remove.isPending}
            onAskDelete={() => setConfirmingDelete(true)}
            onCancelDelete={() => setConfirmingDelete(false)}
            onConfirmDelete={onDelete}
          />
        ) : null}

        {isFailed ? (
          <FailedNotice
            error={entry.error}
            retrying={retry.isPending}
            onRetry={() =>
              retry.mutate(entry.id, {
                onSuccess: () => push({ title: "Back into the darkroom it goes." }),
                onError: (error) =>
                  push({ kind: "problem", title: "Couldn't requeue it.", body: errorMessage(error) }),
              })
            }
          />
        ) : null}
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */

export function FavouriteButton({
  favourite,
  title,
  onToggle,
  className,
}: {
  favourite: boolean;
  title: string;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={favourite}
      aria-label={favourite ? `Remove ${title} from favourites` : `Mark ${title} as a favourite`}
      className={clsx(
        "touch -m-2 flex shrink-0 items-center justify-center p-2 transition-colors",
        favourite ? "text-ochre" : "text-rule hover:text-ink-soft",
        className,
      )}
    >
      {/* A pressed five-petal flower: filled when kept. */}
      <svg viewBox="0 0 24 24" aria-hidden="true" className="size-5">
        {[0, 72, 144, 216, 288].map((angle) => (
          <ellipse
            key={angle}
            cx="12"
            cy="6.4"
            rx="3.1"
            ry="5"
            transform={`rotate(${angle} 12 12)`}
            fill={favourite ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="1.2"
            opacity={favourite ? 0.85 : 1}
          />
        ))}
        <circle cx="12" cy="12" r="1.9" fill="currentColor" />
      </svg>
    </button>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * A rejection isn't a failure of the photograph — it just isn't identifiable.
 * Say so kindly, and offer the only useful action.
 */
export function RejectedNotice({
  reason,
  confirming,
  deleting,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
}: {
  reason: string | null;
  confirming: boolean;
  deleting: boolean;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}) {
  return (
    <div className="mt-4 border-t border-rule pt-3">
      <p className="field-label text-ink-soft">Not determined</p>
      <p className="mt-1.5 font-prose text-[0.95rem] leading-snug text-ink-soft">
        {reason ?? "Claude couldn't make out what this one is."} It's a perfectly good photograph —
        it just isn't a specimen anyone could name.
      </p>
      {confirming ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p className="font-prose text-sm text-ink">Take this page out of the book?</p>
          <button
            type="button"
            onClick={onConfirmDelete}
            disabled={deleting}
            className="touch border border-rust px-3 py-1.5 font-label text-sm text-rust transition-colors hover:bg-[color-mix(in_oklab,var(--color-rust)_10%,transparent)] disabled:opacity-60"
          >
            {deleting ? "Removing…" : "Yes, remove it"}
          </button>
          <button
            type="button"
            onClick={onCancelDelete}
            className="touch font-label text-sm text-ink-soft underline decoration-rule underline-offset-4 hover:text-ink"
          >
            Keep it
          </button>
        </div>
      ) : (
        <button type="button" onClick={onAskDelete} className="link mt-2 font-label text-sm">
          Remove this page
        </button>
      )}
    </div>
  );
}

export function FailedNotice({
  error,
  retrying,
  onRetry,
}: {
  error: string | null;
  retrying: boolean;
  onRetry: () => void;
}) {
  return (
    <div className="mt-4 border-t border-rule pt-3">
      <p className="field-label text-rust">Didn't develop</p>
      <p className="mt-1.5 font-prose text-[0.95rem] leading-snug text-ink-soft">
        {error ?? "Something went wrong on our side, not yours."} The photograph is safe — it just
        needs another pass.
      </p>
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        className="touch mt-3 border border-ink px-3 py-1.5 font-label text-sm text-ink transition-colors hover:bg-paper-deep disabled:opacity-60"
      >
        {retrying ? "Requeuing…" : "Try developing it again"}
      </button>
    </div>
  );
}
